import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Tooltip,
  Button,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import styled from '@emotion/styled';
import { MAX_SIZE_COLUMNS, SIZE_GRID_HEADER_LABELS, toQuantity } from '../../utils/sizeGrids';

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: rgba(255, 255, 255, 0.55);
`;

const SectionHeaderCell = ({ children, colSpan }) => (
  <TableCell
    colSpan={colSpan}
    sx={{
      background: 'var(--al-bg)',
      fontWeight: 700,
      fontSize: '0.8rem',
      letterSpacing: '0.08em',
      borderBottom: '2px solid var(--al-primary)',
      padding: '12px 16px',
    }}
  >
    {children}
  </TableCell>
);

function PurchaseOrderTable({ groups, onFixLink }) {
  if (!groups.length) {
    return (
      <EmptyState>
        <Typography variant="body1" sx={{ mb: 1, color: 'rgba(255,255,255,0.7)' }}>
          No lines selected
        </Typography>
        <Typography variant="body2">
          Select lines from the left panel to build your purchase order.
        </Typography>
      </EmptyState>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        border: 'none',
        overflowX: 'auto',
      }}
    >
      <Table size="small" sx={{ tableLayout: 'auto', minWidth: 720 }}>
        {groups.map(({ gridType, sizeColumns, rows }) => (
          <React.Fragment key={gridType}>
            <TableHead>
              <TableRow>
                <SectionHeaderCell colSpan={MAX_SIZE_COLUMNS + 4}>
                  {SIZE_GRID_HEADER_LABELS[gridType]}
                </SectionHeaderCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ minWidth: 120 }}>Style</TableCell>
                <TableCell sx={{ minWidth: 90 }}>Color</TableCell>
                {sizeColumns.map((size) => (
                  <TableCell key={size} align="center" sx={{ minWidth: 40, px: 0.5 }}>
                    {size}
                  </TableCell>
                ))}
                {Array.from({ length: MAX_SIZE_COLUMNS - sizeColumns.length }).map((_, idx) => (
                  <TableCell key={`pad-${gridType}-${idx}`} sx={{ minWidth: 40 }} />
                ))}
                <TableCell align="right" sx={{ minWidth: 70 }}>
                  Total
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 140 }}>
                  Supplier
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => {
                const total = Object.values(row.sizes || {}).reduce(
                  (sum, qty) => sum + toQuantity(qty),
                  0
                );
                const isCombined = row.lineIds.length > 1;

                return (
                  <TableRow key={`${row.styleNo}-${row.garmentColor}-${idx}`} hover>
                    <TableCell>
                      <Box sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{row.styleNo}</Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <Tooltip
                          title={row.supplierUrl ? 'Supplier URL on file' : 'No supplier URL — add in Products'}
                        >
                          {row.supplierUrl ? (
                            <LinkIcon sx={{ fontSize: 14, color: 'var(--al-primary)' }} />
                          ) : (
                            <LinkOffIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)' }} />
                          )}
                        </Tooltip>
                        {isCombined && (
                          <Typography component="span" sx={{ fontSize: '0.7rem', color: 'var(--al-primary)' }}>
                            ({row.lineIds.length} combined)
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{row.garmentColor}</TableCell>
                    {sizeColumns.map((size) => (
                      <TableCell
                        key={size}
                        align="center"
                        sx={{
                          px: 0.5,
                          fontWeight: toQuantity(row.sizes?.[size]) > 0 ? 600 : 400,
                          color: toQuantity(row.sizes?.[size]) > 0 ? 'inherit' : 'rgba(255,255,255,0.35)',
                        }}
                      >
                        {toQuantity(row.sizes?.[size]) || '—'}
                      </TableCell>
                    ))}
                    {Array.from({ length: MAX_SIZE_COLUMNS - sizeColumns.length }).map((_, padIdx) => (
                      <TableCell key={`pad-row-${padIdx}`} />
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {total}
                    </TableCell>
                    <TableCell align="right">
                      {row.supplierUrl ? (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() =>
                            window.open(row.supplierUrl, '_blank', 'noopener,noreferrer')
                          }
                          sx={{
                            backgroundColor: 'var(--al-primary)',
                            color: 'var(--al-bg)',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            minWidth: 100,
                            '&:hover': { backgroundColor: '#7FFF7F' },
                          }}
                        >
                          Visit
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => onFixLink?.(row)}
                          sx={{
                            backgroundColor: '#ffb74d',
                            color: 'var(--al-bg)',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            minWidth: 100,
                            '&:hover': { backgroundColor: '#ffa726' },
                          }}
                        >
                          Fix Link!
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </React.Fragment>
        ))}
      </Table>
    </TableContainer>
  );
}

export default PurchaseOrderTable;
